import { buildSimpleMetricLegendLabelHtml } from "../../../utils/chart-legend-scale.util";
import { chartTooltipHtmlTheme } from "../../../utils/chart-comparison-tooltip.util";
import Highcharts from "../highcharts.config";

/**
 * Format large numbers for display with K/M suffixes
 */
const formatNumber = (value: number): string => {
  if (value >= 1000000) {
    return (value / 1000000).toFixed(1).replace(/\.0$/, "") + "M";
  } else if (value >= 1000) {
    return (value / 1000).toFixed(1).replace(/\.0$/, "") + "K";
  }
  return value.toLocaleString("en-US");
};

/**
 * Format number with commas and optional decimals
 */
const formatNumberFull = (value: number) => {
  return value
    .toLocaleString("en-US", {
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    });
};

export function createDonutChartTooltipFormatter(
  pinnedThemeMode?: "light" | "dark"
): Highcharts.TooltipFormatterCallbackFunction {
  const theme = pinnedThemeMode
    ? chartTooltipHtmlTheme(pinnedThemeMode)
    : null;
  const textSecondary =
    theme?.textSecondary ?? "var(--text-secondary-color)";
  const textPrimary = theme?.textPrimary ?? "var(--text-primary-color)";
  const background =
    theme?.background ?? "var(--background-primary-color)";

  return function (this: any) {
    const point = this.point;
    const total = point.series.data.reduce(
      (sum: number, p: any) => sum + p.y,
      0
    );
    const percentage = total > 0
      ? (((point.y ?? 0) / total) * 100).toFixed(1)
      : "0";

    return `
        <div style="padding: 8px; background: ${background};">
          <div style="font-size: var(--font-size-xs); font-weight: var(--font-weight-regular); color: ${textSecondary}; margin-bottom: 8px;">
            ${point.name}
          </div>
          <div style="display: flex; align-items: center;">
            <div style="width: 3px; height: 30px; background-color: ${point.color}; margin-right: 8px; border-radius: 2px;"></div>
            <div style="display: flex; flex-direction: column;">
              <span style="color: ${textPrimary}; font-weight: var(--font-weight-regular); font-size: var(--font-size-sm);">
                ${formatNumberFull(point.y ?? 0)}
              </span>
              <span style="color: ${textSecondary}; font-size: var(--font-size-xs);">
                ${percentage}%
              </span>
            </div>
          </div>
        </div>
      `;
  };
}

export const donutChartDefaults: Highcharts.Options = {
  chart: {
    type: "pie",
    animation: true,
    style: {
      fontFamily: "var(--font-family, -apple-system, system-ui, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif)",
    },
    backgroundColor: "transparent",
    spacing: [10, 20, 10, 20],
  },
  title: {
    text: undefined,
  },
  credits: {
    enabled: false,
  },
  plotOptions: {
    pie: {
      innerSize: "60%",
      borderWidth: 0,
      borderRadius: 4,
      dataLabels: {
        enabled: false,
      },
      showInLegend: true,
      states: {
        hover: {
          brightness: 0.1,
          halo: {
            size: 5,
          },
        },
        inactive: {
          opacity: 0.5,
        },
      },
      center: ["50%", "50%"],
    },
  },
  tooltip: {
    outside: true,
    enabled: true,
    useHTML: true,
    shadow: {
      color: "var(--card-shadow-color)",
      opacity: 1,
      offsetX: 0,
      offsetY: 4,
      width: 12,
    },
    backgroundColor: "var(--background-primary-color)",
    borderColor: "var(--border-primary-color)",
    borderWidth: 1,
    borderRadius: 8,
    style: {
      color: "var(--text-primary-color)",
      fontSize: "var(--font-size-sm)",
    },
    formatter: createDonutChartTooltipFormatter(),
  },
  legend: {
    enabled: true,
    align: "left",
    verticalAlign: "top",
    layout: "horizontal",
    useHTML: true,
    itemStyle: {
      color: "var(--text-primary-color)",
      fontWeight: "normal",
      fontSize: "var(--font-size-md)",
    },
    itemHoverStyle: {
      color: "var(--text-primary-color)",
    },
    symbolWidth: 0,
    symbolHeight: 0,
    symbolRadius: 0,
    symbolPadding: 0,
    squareSymbol: false,
    itemDistance: 24,
    itemMarginBottom: 0,
    navigation: {
      activeColor: "var(--text-link-color)",
      inactiveColor: "var(--text-secondary-color)",
      animation: true,
    },
    labelFormatter: function (this: any) {
      const point = this;
      const total =
        point.series?.data?.reduce((sum: number, p: any) => sum + p.y, 0) ||
        0;
      const percentage =
        total > 0 ? ((point.y / total) * 100).toFixed(1) : "0";

      return buildSimpleMetricLegendLabelHtml({
        seriesName: point.name,
        color: point.color,
        primaryText: formatNumber(point.y),
        secondaryText: `${percentage}%`,
      });
    },
  },
};
